import React, { useEffect, useRef } from 'react';
import { Animated, Platform, View } from 'react-native';
import { createStyles } from '@/contexts/theme-context';
import { EXTRA_SMALL_MAX_WIDTH } from './welcome-page-container';
import { SCREEN_PADDING, SCREEN_PADDING_SMALL, TYPOGRAPHY } from '@/styles';
import { useAuthStackContext } from '@/contexts/auth-stack-context';
import { useLayoutContext } from '@/contexts/layout-context';
import { useMediaQuery } from 'react-responsive';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { WelcomePageButton } from './welcome-page-button';
import { WelcomePageButtonConfig } from './welcome-page-types';

interface Props {
  welcomeButtons: WelcomePageButtonConfig[];
  welcomeScreenActive: boolean;
}

export const WelcomeContainerContent: React.FC<Props> = ({
  welcomeButtons,
  welcomeScreenActive,
}) => {
  const [styles, theme] = useStyles();
  const { isLargeLayout, isSmallLayout } = useLayoutContext();
  const isExtraSmallLayout = useMediaQuery({
    maxWidth: EXTRA_SMALL_MAX_WIDTH,
  });
  const { bottom } = useSafeAreaInsets();
  const { appBackgroundVideoUrl, appBackgroundImageUrl, tagline } =
    useAuthStackContext();

  const taglineOpacityRef = useRef(
    new Animated.Value(welcomeScreenActive ? 1 : 0),
  );

  useEffect(() => {
    Animated.timing(taglineOpacityRef.current, {
      duration: welcomeScreenActive ? 250 : 0,
      toValue: welcomeScreenActive ? 1 : 0,
      useNativeDriver: true,
    }).start();
  }, [welcomeScreenActive]);

  return (
    <View
      pointerEvents="box-none"
      style={[
        styles.contentContainer,
        {
          paddingBottom:
            Math.max(bottom, SCREEN_PADDING) + SCREEN_PADDING_SMALL,
        },
        Platform.OS === 'web' && welcomeScreenActive
          ? styles.contentContainerWeb
          : null,
      ]}
    >
      {Platform.OS === 'web' ? (
        <View
          pointerEvents="none"
          style={
            isSmallLayout || isExtraSmallLayout ? styles.taglineContainer : null
          }
        >
          <Animated.Text
            style={[
              TYPOGRAPHY.h1,
              styles.tagline,
              isLargeLayout ? styles.centeredText : null,
              isExtraSmallLayout
                ? styles.taglineExtraSmallFont
                : isSmallLayout
                  ? styles.tagLineSmallFont
                  : null,
              { opacity: taglineOpacityRef.current },
              {
                color:
                  appBackgroundVideoUrl || appBackgroundImageUrl
                    ? theme.colors.white
                    : theme.colors.primary,
              },
            ]}
          >
            {tagline}
          </Animated.Text>
        </View>
      ) : null}
      <View
        style={[
          styles.buttonsContainer,
          !welcomeScreenActive || (isLargeLayout && welcomeButtons.length !== 3)
            ? styles.buttonsContainerRow
            : styles.buttonsContainerColumn,
        ]}
      >
        {welcomeButtons.map((buttonConfig, index) => (
          <WelcomePageButton
            appBackgroundImageUrl={appBackgroundImageUrl || undefined}
            appBackgroundVideoUrl={appBackgroundVideoUrl || undefined}
            buttonConfig={buttonConfig}
            key={`${buttonConfig.trackingName}-${index}`}
            welcomeScreenActive={welcomeScreenActive}
          />
        ))}
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  buttonsContainer: {
    justifyContent: 'center',
    width: '100%',
  },
  buttonsContainerColumn: {
    alignItems: 'center',
    flexDirection: 'column',
  },
  buttonsContainerRow: {
    flexDirection: 'row',
    gap: 8,
  },
  centeredText: {
    textAlign: 'center',
  },
  contentContainer: {
    height: '100%',
    justifyContent: 'flex-end',
    paddingHorizontal: SCREEN_PADDING,
    position: 'absolute',
    width: '100%',
  },
  contentContainerWeb: {
    justifyContent: 'center',
  },
  tagline: {
    color: theme.colors.white,
    fontSize: 46,
    lineHeight: 54,
    marginHorizontal: 'auto',
    maxWidth: 500,
    paddingVertical: 12,
  },
  taglineContainer: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  taglineExtraSmallFont: {
    fontSize: 30,
    lineHeight: 38,
  },
  tagLineSmallFont: {
    fontSize: 40,
    lineHeight: 48,
  },
}));
